import React from "react";
import { Link } from "react-router-dom";
import { Phone, CalendarHeart, ArrowRight, Check, Sun, Coffee, UtensilsCrossed, Sparkles, Moon,
  Utensils, HandHeart, Stethoscope, Flower2, ClipboardPlus, Car, Scissors, Quote } from "lucide-react";
import { useI18n } from "../i18n/I18nContext";
import { useContent } from "../hooks/useContent";
import { useSeo } from "../components/SEO";
import { Section, Eyebrow, StatusBadge } from "../components/Primitives";
import { Button } from "../components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";

const PHONE = "450-736-0748";
const ICONS = { utensils: Utensils, "hand-heart": HandHeart, sparkles: Sparkles, stethoscope: Stethoscope,
  flower: Flower2, "clipboard-plus": ClipboardPlus, car: Car, scissors: Scissors };
const DAYICONS = [Sun, Coffee, UtensilsCrossed, Sparkles, Moon];

export default function Home() {
  const { t, pick } = useI18n();
  useSeo("Résidence privée pour aînés à Laval",
    "Résidence Doux Instants — RPA de catégorie 4 à Laval (Auteuil). Soins 24/7, milieu de vie chaleureux, repas inclus. Planifiez une visite.");

  const { data: settings = {} } = useContent("settings");
  const { data: services = [] } = useContent("services");
  const { data: gallery = [] } = useContent("gallery");
  const { data: testimonials = [] } = useContent("testimonials");
  const { data: faq = [] } = useContent("faq");

  return (
    <>
      {/* HERO */}
      <section className="relative overflow-hidden bg-[#F9F6F0]" data-testid="hero">
        <div className="mx-auto grid max-w-6xl items-center gap-10 px-5 py-16 sm:px-8 sm:py-24 lg:grid-cols-2">
          <div className="fade-up">
            <Eyebrow>Laval · Auteuil</Eyebrow>
            <h1 className="mt-4 font-display text-4xl leading-tight tracking-tight sm:text-5xl lg:text-6xl">
              {pick(settings.hero_title) || "Bienvenue dans un nouveau départ"}
            </h1>
            <p className="mt-5 max-w-xl text-lg text-[#5C6370]">
              {pick(settings.hero_subtitle)}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button asChild className="rounded-full min-h-[48px] bg-[#2A9D8F] px-7 hover:bg-[#21867A]">
                <Link to="/contact" data-testid="hero-plan-visit">
                  <CalendarHeart className="mr-2 h-5 w-5" /> {t("common.planVisit")}
                </Link>
              </Button>
              <Button asChild variant="outline" className="rounded-full min-h-[48px] border-[#2A9D8F]/40 px-7 text-[#2D3142]">
                <a href={`tel:${PHONE}`} data-testid="hero-call"><Phone className="mr-2 h-5 w-5" /> {PHONE}</a>
              </Button>
            </div>
          </div>
          <div className="fade-up" style={{ animationDelay: "0.15s" }}>
            <img src="https://images.unsplash.com/photo-1786912029670-a3361977ab4f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
              alt="Aînée sereine au jardin de la résidence" loading="eager"
              className="aspect-[4/3] w-full rounded-3xl object-cover shadow-lg" />
          </div>
        </div>
        {/* TRUST BAR — confirmed items only */}
        <div className="border-y border-black/5 bg-white" data-testid="trust-bar">
          <div className="mx-auto flex max-w-6xl flex-wrap justify-center gap-x-10 gap-y-3 px-5 py-5 sm:px-8">
            {t("home.trustBar").map((item) => (
              <span key={item} className="flex items-center gap-2 text-sm font-medium text-[#2D3142]">
                <Check className="h-4 w-4 text-[#2A9D8F]" /> {item}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* PRÉSENTATION */}
      <Section tone="base" data-testid="presentation">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <img src="https://images.pexels.com/photos/8086761/pexels-photo-8086761.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
            alt="Moment chaleureux entre aînés" loading="lazy"
            className="aspect-[4/3] w-full rounded-3xl object-cover shadow-md" />
          <div>
            <Eyebrow>{t("nav.residence")}</Eyebrow>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl">{t("home.presentationTitle")}</h2>
            <p className="mt-4 text-lg text-[#5C6370]">{t("home.presentationBody")}</p>
            <Button asChild variant="link" className="mt-4 px-0 text-[#2A9D8F]">
              <Link to="/la-residence" data-testid="home-residence-link">{t("common.learnMore")} <ArrowRight className="ml-1 h-4 w-4" /></Link>
            </Button>
          </div>
        </div>
      </Section>

      {/* À QUI S'ADRESSE */}
      <Section tone="surface" data-testid="audience">
        <h2 className="font-display text-3xl sm:text-4xl">{t("home.audienceTitle")}</h2>
        <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {t("home.audience").map((a) => (
            <div key={a.t} className="rounded-2xl border border-black/5 bg-[#F9F6F0] p-6 transition-transform hover:-translate-y-1">
              <h3 className="font-display text-xl">{a.t}</h3>
              <p className="mt-2 text-[15px] text-[#5C6370]">{a.d}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* SERVICES — comparable cards */}
      <Section tone="sage" data-testid="services-cards">
        <Eyebrow>{t("home.servicesTitle")}</Eyebrow>
        <h2 className="mt-3 font-display text-3xl sm:text-4xl">{t("home.servicesIntro")}</h2>
        <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {services.slice(0, 6).map((s) => {
            const Icon = ICONS[s.icon] || Sparkles;
            return (
              <div key={s.id} className="flex flex-col rounded-2xl border border-black/5 bg-white p-6">
                <div className="flex items-start justify-between gap-3">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#2A9D8F]/10">
                    <Icon className="h-6 w-6 text-[#2A9D8F]" />
                  </span>
                  <StatusBadge status={s.status} label={t(`serviceStatus.${s.status}`)} />
                </div>
                <h3 className="mt-4 font-display text-xl">{pick(s.name)}</h3>
                <p className="mt-2 text-[15px] text-[#5C6370]">{pick(s.desc)}</p>
              </div>
            );
          })}
        </div>
        <Button asChild variant="link" className="mt-6 px-0 text-[#1E293B]">
          <Link to="/services" data-testid="home-services-link">{t("common.viewAll")} <ArrowRight className="ml-1 h-4 w-4" /></Link>
        </Button>
      </Section>

      {/* MILIEU DE VIE */}
      <Section tone="base" data-testid="milieu-preview">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <div>
            <Eyebrow>{t("nav.milieu")}</Eyebrow>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl">{t("home.milieuTitle")}</h2>
            <p className="mt-4 text-lg text-[#5C6370]">{t("home.milieuBody")}</p>
            <Button asChild variant="link" className="mt-4 px-0 text-[#2A9D8F]">
              <Link to="/milieu-de-vie" data-testid="home-milieu-link">{t("common.learnMore")} <ArrowRight className="ml-1 h-4 w-4" /></Link>
            </Button>
          </div>
          <img src="https://images.pexels.com/photos/36486485/pexels-photo-36486485.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
            alt="Salon commun lumineux" loading="lazy" className="aspect-[4/3] w-full rounded-3xl object-cover shadow-md" />
        </div>
      </Section>

      {/* JOURNÉE TYPE */}
      <Section tone="surface" data-testid="journee-timeline">
        <h2 className="font-display text-3xl sm:text-4xl">{t("home.journeeTitle")}</h2>
        <ol className="mt-8 space-y-4 border-l-2 border-[#2A9D8F]/25 pl-6">
          {t("home.journee").map((j, i) => {
            const Icon = DAYICONS[i] || Sun;
            return (
              <li key={j.h} className="relative">
                <span className="absolute -left-[38px] flex h-8 w-8 items-center justify-center rounded-full bg-[#2A9D8F] text-white">
                  <Icon className="h-4 w-4" />
                </span>
                <p className="font-display text-lg">{j.h}</p>
                <p className="text-[15px] text-[#5C6370]">{j.d}</p>
              </li>
            );
          })}
        </ol>
      </Section>

      {/* PROCESSUS ADMISSION */}
      <Section tone="sage" data-testid="admission-preview">
        <h2 className="font-display text-3xl sm:text-4xl">{t("home.admissionTitle")}</h2>
        <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {t("home.admission").map((a, i) => (
            <div key={a.t} className="rounded-2xl border border-black/5 bg-white p-6">
              <span className="font-display text-3xl text-[#2A9D8F]">{i + 1}</span>
              <h3 className="mt-2 font-display text-lg">{a.t}</h3>
              <p className="mt-1 text-[15px] text-[#5C6370]">{a.d}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* GALERIE */}
      <Section tone="base" data-testid="gallery-preview">
        <div className="flex items-end justify-between">
          <h2 className="font-display text-3xl sm:text-4xl">{t("home.galleryTitle")}</h2>
          <Button asChild variant="link" className="px-0 text-[#2A9D8F]">
            <Link to="/galerie">{t("common.viewAll")} <ArrowRight className="ml-1 h-4 w-4" /></Link>
          </Button>
        </div>
        <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {gallery.filter((g) => g.image).slice(0, 4).map((g) => (
            <img key={g.id} src={g.image} alt={pick(g.caption)} loading="lazy"
              className="aspect-square w-full rounded-2xl object-cover" />
          ))}
        </div>
      </Section>

      {/* TÉMOIGNAGES */}
      {testimonials.length > 0 && (
        <Section tone="surface" data-testid="testimonials">
          <h2 className="font-display text-3xl sm:text-4xl">{t("home.testimonialsTitle")}</h2>
          <div className="mt-8 grid gap-5 md:grid-cols-3">
            {testimonials.map((tm) => (
              <blockquote key={tm.id} className="rounded-2xl border border-black/5 bg-[#F9F6F0] p-6">
                <Quote className="h-7 w-7 text-[#D4B499]" />
                <p className="mt-3 text-[15px] italic text-[#2D3142]">"{pick(tm.quote)}"</p>
                <footer className="mt-4 text-sm font-semibold">{tm.author}<span className="font-normal text-[#5C6370]"> · {pick(tm.detail)}</span></footer>
              </blockquote>
            ))}
          </div>
        </Section>
      )}

      {/* FAQ */}
      <Section tone="base" data-testid="faq-accordion">
        <h2 className="font-display text-3xl sm:text-4xl">{t("home.faqTitle")}</h2>
        <Accordion type="single" collapsible className="mt-6">
          {faq.slice(0, 5).map((f) => (
            <AccordionItem key={f.id} value={f.id}>
              <AccordionTrigger className="text-left text-lg">{pick(f.q)}</AccordionTrigger>
              <AccordionContent className="text-[15px] text-[#5C6370]">{pick(f.a)}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Section>

      {/* CTA FINAL */}
      <Section tone="slate" data-testid="final-cta">
        <div className="text-center">
          <h2 className="font-display text-3xl text-white sm:text-4xl">{t("home.ctaTitle")}</h2>
          <p className="mx-auto mt-3 max-w-xl text-white/80">{t("home.ctaBody")}</p>
          <div className="mt-7 flex flex-wrap justify-center gap-3">
            <Button asChild className="rounded-full min-h-[48px] bg-[#2A9D8F] px-7 hover:bg-[#21867A]">
              <Link to="/contact" data-testid="cta-plan-visit"><CalendarHeart className="mr-2 h-5 w-5" /> {t("common.planVisit")}</Link>
            </Button>
            <Button asChild variant="outline" className="rounded-full min-h-[48px] border-white/30 bg-transparent px-7 text-white hover:bg-white/10">
              <a href={`tel:${PHONE}`} data-testid="cta-call"><Phone className="mr-2 h-5 w-5" /> {PHONE}</a>
            </Button>
          </div>
        </div>
      </Section>
    </>
  );
}
